/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SessionExpiryProvider } from './context/SessionExpiryContext';
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from './theme/ThemeContext';
import { NotificationProvider } from './context/NotificationContext';

// `new QueryClient()` with no defaults leaves refetchOnWindowFocus ON, which is
// the single largest source of repeat traffic in this app: every alt-tab back
// into the browser refired every mounted query at once. A public page with four
// data-bound sections meant five requests for doing nothing but switching tabs.
//
// Only the focus trigger is turned off. Queries still refetch on mount and on
// navigation, and every explicit refetch() call is untouched, so nothing that
// deliberately reloads data changes behaviour. A screen that genuinely needs
// focus revalidation can opt back in per query with
// `refetchOnWindowFocus: true`.
//
// staleTime is deliberately NOT set here: the public hooks each pass their own
// `staleTime: 0`, which would override a default anyway. Raising those is a
// separate decision, taken per hook.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <SessionExpiryProvider>
            <NotificationProvider>
              <App />
            </NotificationProvider>
          </SessionExpiryProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
